import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './SOPParameters.css';

const SOPParameters = () => {
  const [activeTab, setActiveTab] = useState('Sales Forecasting');
  const [configs, setConfigs] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  // Fetch configuration data on component mount
  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/planning-config');
      if (response.data.success) {
        // Group configs by category
        const groupedConfigs = {};
        response.data.configs.forEach(config => {
          if (!groupedConfigs[config.config_category]) {
            groupedConfigs[config.config_category] = [];
          }
          groupedConfigs[config.config_category].push(config);
        });
        setConfigs(groupedConfigs);
      }
    } catch (error) {
      console.error('Error fetching configs:', error);
      setMessage('Error loading configuration data');
    } finally {
      setLoading(false);
    }
  };

  const handleConfigChange = (configName, newValue) => {
    setConfigs(prevConfigs => {
      const newConfigs = { ...prevConfigs };
      Object.keys(newConfigs).forEach(category => {
        newConfigs[category] = newConfigs[category].map(config => {
          if (config.config_name === configName) {
            return { ...config, config_value: parseFloat(newValue) || 0 };
          }
          return config;
        });
      });
      return newConfigs;
    });
  };

  const saveConfig = async (configName, newValue) => {
    try {
      setSaving(true);
      const response = await axios.put(`/api/planning-config/${configName}`, {
        config_value: newValue
      });
      
      if (response.data.success) {
        setMessage(`Configuration ${configName} updated successfully`);
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error saving config:', error);
      setMessage(`Error saving ${configName}: ${error.response?.data?.error || error.message}`);
      setTimeout(() => setMessage(''), 5000);
    } finally {
      setSaving(false);
    }
  };

  const saveAllConfigs = async () => {
    try {
      setSaving(true);
      const promises = [];
      
      Object.values(configs).flat().forEach(config => {
        promises.push(
          axios.put(`/api/planning-config/${config.config_name}`, {
            config_value: config.config_value
          })
        );
      });
      
      await Promise.all(promises);
      setMessage('All configurations saved successfully');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error saving all configs:', error);
      setMessage('Error saving configurations');
      setTimeout(() => setMessage(''), 5000);
    } finally {
      setSaving(false);
    }
  };

  const renderConfigInput = (config) => {
    const handleBlur = () => {
      saveConfig(config.config_name, config.config_value);
    };

    const handleKeyPress = (e) => {
      if (e.key === 'Enter') {
        e.target.blur();
      }
    };

    return (
      <div key={config.config_name} className="parameter-item">
        <label>{config.config_name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</label>
        <input
          type="number"
          value={config.config_value}
          onChange={(e) => handleConfigChange(config.config_name, e.target.value)}
          onBlur={handleBlur}
          onKeyPress={handleKeyPress}
          step="any"
          min="0"
        />
        <span className="parameter-description">{config.config_description}</span>
      </div>
    );
  };

  const renderTabContent = () => {
    if (loading) {
      return (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading configuration data...</p>
        </div>
      );
    }

    const categoryMap = {
      'forecast': 'Sales Forecasting',
      'production': 'Production Planning', 
      'component': 'Component Ordering'
    };

    const currentCategory = Object.keys(categoryMap).find(key => categoryMap[key] === activeTab);
    const currentConfigs = configs[currentCategory] || [];

    return (
      <div className={`${currentCategory}-section`}>
        <div className="section-header">
          <h2>{activeTab} Parameters</h2>
          <div className="header-actions">
            <button 
              className="save-all-button" 
              onClick={saveAllConfigs}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save All Changes'}
            </button>
          </div>
        </div>
        
        {message && (
          <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
            {message}
          </div>
        )}
        
        <div className="content-grid">
          <div className="card">
            <h3>Configuration Parameters</h3>
            <div className="parameter-grid">
              {currentConfigs.map(renderConfigInput)}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="sop-parameters">
      <div className="page-header">
        <div className="header-content">
          <div className="header-title">
            <h1>S&OP Parameters Manager</h1>
          </div>
          <div className="header-tips">
            <ul>
              <li>Configure forecasting and production parameters</li>
              <li>Set inventory management and reorder settings</li>
              <li>Customize S&OP planning algorithms and thresholds</li>
            </ul>
          </div>
        </div>
        <div className="header-separator"></div>
      </div>

      <div className="page-content">
        <div className="tab-container">
          <div className="tab-buttons">
            <button 
              className={`tab-button ${activeTab === 'Sales Forecasting' ? 'active' : ''}`}
              onClick={() => setActiveTab('Sales Forecasting')}
            >
              Sales Forecasting
            </button>
            <button 
              className={`tab-button ${activeTab === 'Production Planning' ? 'active' : ''}`}
              onClick={() => setActiveTab('Production Planning')}
            >
              Production Planning
            </button>
            <button 
              className={`tab-button ${activeTab === 'Component Ordering' ? 'active' : ''}`}
              onClick={() => setActiveTab('Component Ordering')}
            >
              Component Ordering
            </button>
          </div>

          <div className="tab-content">
            {renderTabContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SOPParameters; 